import React, { useState } from 'react'
import {
  Divider,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Stack,
  Button,
  CircularProgress,
  Alert
} from '@mui/material'
import { ApprovedAccount } from 'types/accountTypes'
import { impersonateAccount } from 'api/accounts'
import EditDiscountRateForm from 'pages/AccountDetail/EditDiscountRateForm'
import { formatDisplayValue } from 'utils'

const ApprovedAccountInfo = ({ data }: { data: ApprovedAccount }) => {
  const {
    id,
    email,
    firstName,
    lastName,
    discountRates,
    isTaxable,
    authorizeCustomerId,
    netsuiteCustomerId,
    role,
    createdAt,
    updatedAt,
    addresses
  } = data

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleImpersonate = async (targetAccountId: string) => {
    setIsLoading(true)
    setError(null)
    try {
      await impersonateAccount(targetAccountId)
    } catch (e: any) {
      setError(e || 'Failed to login as user')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box>
      <Box mt={5} textAlign='center'>
        <Button
          variant='contained'
          color='primary'
          onClick={() => handleImpersonate(id)}
          size='large'
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Logging in...' : 'Login as This User'}
        </Button>
      </Box>
      {error && (
        <Box mt={2}>
          <Alert severity='error' onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}
      <Box my={3}>
        <Typography variant='h6'>Account Detail</Typography>
        <Divider />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <strong>ID:</strong> {id}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Email:</strong> {email}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>First Name:</strong> {firstName}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Last Name:</strong> {lastName}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Company Name:</strong> {formatDisplayValue(data.companyName)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Taxable:</strong> {isTaxable ? 'Yes' : 'No'}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Discount Rates:</strong>{' '}
          <Box component='span' sx={{ fontFamily: 'monospace', fontSize: 13 }}>
            {discountRates ? JSON.stringify(discountRates) : '—'}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Authorize Customer ID:</strong>{' '}
          {formatDisplayValue(authorizeCustomerId)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>NetSuite Customer ID:</strong> {netsuiteCustomerId}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Role:</strong> {role}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Created At:</strong>{' '}
          {new Date(createdAt).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Updated At:</strong>{' '}
          {new Date(updatedAt).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Grid>
      </Grid>

      <Box my={3}>
        <Typography variant='h6'>Application Info</Typography>
        <Divider />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <strong>Alternate Phone:</strong>{' '}
          {formatDisplayValue(data.alternatePhoneNumber)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Fax:</strong> {formatDisplayValue(data.fax)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Sales Rep:</strong>{' '}
          {formatDisplayValue(data.salesRepresentative)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Tax ID / EIN:</strong> {formatDisplayValue(data.taxId)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Business Type:</strong>{' '}
          {formatDisplayValue(data.businessType)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Expected Monthly Sales:</strong>{' '}
          {formatDisplayValue(data.expectedMonthlySales)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Sales Tax Account:</strong>{' '}
          {formatDisplayValue(data.salesTaxAccount)}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Resale License:</strong>{' '}
          {data.hasResaleLicense
            ? 'Yes'
            : data.hasResaleLicense === false
            ? 'No'
            : '—'}
        </Grid>
        <Grid item xs={12} sm={6}>
          <strong>Resale License Number:</strong>{' '}
          {formatDisplayValue(data.resaleLicenseNumber)}
        </Grid>
        <Grid item xs={12}>
          <strong>Notes:</strong> {formatDisplayValue(data.notes)}
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />
      <EditDiscountRateForm data={data} />

      {addresses && addresses.length > 0 && (
        <>
          <Box my={3}>
            <Typography variant='h6'>Addresses</Typography>
            <Divider />
          </Box>
          <Grid container spacing={2}>
            {addresses.map(
              ({
                id,
                firstName,
                lastName,
                companyName,
                addressLine1,
                addressLine2,
                city,
                state,
                zipCode,
                phoneNumber,
                isDefaultBilling,
                isDefaultShipping
              }) => (
                <Grid item xs={12} md={6} key={id}>
                  <Card variant='outlined'>
                    <CardContent>
                      <Stack spacing={0.5}>
                        <Typography variant='subtitle1'>
                          {firstName} {lastName}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          {companyName && `${companyName}, `}
                          {addressLine1}
                          {addressLine2 && `, ${addressLine2}`}, {city}, {state}{' '}
                          {zipCode}
                        </Typography>
                        <Typography variant='body2'>
                          Phone: {phoneNumber}
                        </Typography>
                        {(isDefaultBilling || isDefaultShipping) && (
                          <Typography
                            variant='body2'
                            sx={{ color: 'primary.main' }}
                          >
                            {[
                              isDefaultBilling && 'Default Billing',
                              isDefaultShipping && 'Default Shipping'
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              )
            )}
          </Grid>
        </>
      )}
    </Box>
  )
}

export default ApprovedAccountInfo
